import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UploadCloud, Loader2, FileText, Check, PenLine, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { createTx, extractFile, CUR_FULL } from './api';

const today = () => new Date().toISOString().slice(0, 10);

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</span>{children}</label>
);

const TxForm = ({ type, meta, token, onSaved, prefill }) => {
  const cats = type === 'income' ? meta.income_categories : meta.expense_categories;
  const [amount, setAmount] = useState(prefill?.amount || '');
  const [gst, setGst] = useState(prefill?.gst || '');
  const [date, setDate] = useState(prefill?.date || today());
  const [merchant, setMerchant] = useState(prefill?.merchant || '');
  const [category, setCategory] = useState(prefill?.category || cats[0]);
  const [description, setDescription] = useState(prefill?.description || '');
  const [recurring, setRecurring] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!amount || Number(amount) <= 0) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try {
      await createTx(token, { type, amount: Number(amount), gst: Number(gst || 0), date, merchant, category, description, recurring, file_id: prefill?.file_id });
      toast.success(`${type === 'income' ? 'Income' : 'Expense'} saved`);
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`tx-form-${type}`}>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`Amount (${meta.settings.currency})`}><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="tx-amount" autoFocus /></Field>
        <Field label="GST portion"><Input type="number" value={gst} onChange={(e) => setGst(e.target.value)} placeholder="0.00" data-testid="tx-gst" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="tx-date" /></Field>
        <Field label="Category">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="tx-category"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label={type === 'income' ? 'Source' : 'Merchant / supplier'}><Input value={merchant} onChange={(e) => setMerchant(e.target.value)} placeholder={type === 'income' ? 'e.g. Aggregator payout' : 'e.g. Meta, Officeworks'} data-testid="tx-merchant" /></Field>
      <Field label="Description (optional)"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="tx-desc" /></Field>
      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="tx-recurring" /> This is a recurring {type === 'income' ? 'income' : 'expense'}</label>
      <Button onClick={save} disabled={busy} className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-700" data-testid="tx-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" /> Save {type}</>}</Button>
    </div>
  );
};

const UploadTab = ({ meta, token, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef();

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setFileName(file.name);
    setSuggestions(null);
    try {
      const r = await extractFile(token, file);
      if (!r.transactions?.length) { toast.error('Nothing readable found — enter it manually.'); }
      setSuggestions(r.transactions.map((t) => ({ ...t, file_id: r.file_id })));
      if (r.transactions?.length) toast.success(`Found ${r.transactions.length} transaction${r.transactions.length !== 1 ? 's' : ''}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (suggestions && suggestions.length) {
    return (
      <div className="space-y-4" data-testid="upload-suggestions">
        <div className="flex items-center gap-2 text-sm text-lime-700 bg-lime-50 rounded-lg px-3 py-2"><FileText className="w-4 h-4" /> Read <b>{fileName}</b> — review & save below</div>
        {suggestions.map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">
              {s.type === 'income' ? <TrendingUp className="w-3.5 h-3.5 text-lime-600" /> : <TrendingDown className="w-3.5 h-3.5 text-rose-600" />} {s.type} · {CUR_FULL(s.amount)}
            </div>
            <TxForm type={s.type} meta={meta} token={token} onSaved={onSaved} prefill={s} />
          </div>
        ))}
        <button onClick={() => { setSuggestions(null); setFileName(''); }} className="text-xs text-slate-500 hover:text-slate-900" data-testid="upload-another">Upload another</button>
      </div>
    );
  }

  return (
    <div data-testid="upload-tab">
      <input ref={inputRef} type="file" accept="image/*,application/pdf,.csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="upload-input" />
      <button onClick={() => inputRef.current?.click()} disabled={busy}
        className="w-full rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-900 transition-colors py-12 flex flex-col items-center gap-3 text-slate-500 hover:text-slate-900">
        {busy ? <><Loader2 className="w-8 h-8 animate-spin" /><span className="text-sm font-semibold">Reading your document…</span></>
          : <><UploadCloud className="w-9 h-9" /><span className="text-sm font-bold">Take photo · screenshot · receipt · invoice · PDF</span><span className="text-xs">DerwentAI reads the merchant, amount, GST, date & category</span></>}
      </button>
    </div>
  );
};

export const AddTransactionDialog = ({ open, onOpenChange, meta, token, onSaved }) => {
  const [tab, setTab] = useState('expense');
  if (!meta) return null;
  const done = () => { onSaved(); onOpenChange(false); };
  const TABS = [['expense', 'Expense', TrendingDown], ['income', 'Income', TrendingUp], ['upload', 'Upload', UploadCloud]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-tx-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5" /> Add Transaction</DialogTitle></DialogHeader>
        <div className="flex gap-1.5 p-1 bg-slate-100 rounded-full mb-3">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tx-tab-${k}`}
              className={cn('flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold transition-colors', tab === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800')}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        {tab === 'upload' ? <UploadTab meta={meta} token={token} onSaved={done} />
          : <TxForm key={tab} type={tab} meta={meta} token={token} onSaved={done} />}
      </DialogContent>
    </Dialog>
  );
};

export default AddTransactionDialog;
